import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { isCompanyAdmin } from '../utils/planAccess';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import ColumnCustomizer from '../components/ColumnCustomizer';
import EmptyState from '../components/EmptyState';
import { useConfirm } from '../context/ConfirmContext';
import './Tasks.css';
import './Articles.css';

const UNIT_MODES = ['quantity', 'time', 'both'];

const Articles = () => {
  const { t } = useTranslation(['articles', 'common']);
  const confirm = useConfirm();
  const { user } = useContext(AuthContext);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    unitMode: 'quantity',
    description: ''
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    unitMode: true,
    description: true,
    company: true
  });
  const [sorting, setSorting] = useState({ column: null, direction: null });

  const canManage = isCompanyAdmin(user);
  const showCompanyColumn = articles.some((article) => article.company_name);

  const articleColumnLabels = useMemo(() => {
    const labels = {
      name: t('common:name'),
      unitMode: t('articles:columnUnitMode'),
      description: t('articles:description')
    };
    if (showCompanyColumn) {
      labels.company = t('common:company');
    }
    return labels;
  }, [t, showCompanyColumn]);

  useEffect(() => {
    fetchArticles();
  }, [user]);

  const unitModeLabel = (mode) => {
    if (mode === 'quantity') return t('articles:unitModeQuantity');
    if (mode === 'time') return t('articles:unitModeTime');
    if (mode === 'both') return t('articles:unitModeBoth');
    return mode || t('common:emptyCell');
  };

  const handleSort = (column) => {
    setSorting((prev) => {
      if (prev.column === column) {
        if (prev.direction === 'asc') {
          return { column, direction: 'desc' };
        }
        if (prev.direction === 'desc') {
          return { column: null, direction: null };
        }
      }
      return { column, direction: 'asc' };
    });
  };

  const renderSortableHeader = (column, label) => {
    const isSorted = sorting.column === column;
    const sortDirection = isSorted ? sorting.direction : null;

    return (
      <th
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => handleSort(column)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>{label}</span>
          {sortDirection === 'asc' && (
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_upward</span>
          )}
          {sortDirection === 'desc' && (
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_downward</span>
          )}
        </div>
      </th>
    );
  };

  const sortArticles = (list) => {
    if (!sorting.column || !sorting.direction) {
      return list;
    }

    return [...list].sort((a, b) => {
      let aValue;
      let bValue;

      switch (sorting.column) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'unitMode':
          aValue = (a.unit_mode || '').toLowerCase();
          bValue = (b.unit_mode || '').toLowerCase();
          break;
        case 'description':
          aValue = (a.description || '').toLowerCase();
          bValue = (b.description || '').toLowerCase();
          break;
        case 'company':
          aValue = (a.company_name || '').toLowerCase();
          bValue = (b.company_name || '').toLowerCase();
          break;
        default:
          return 0;
      }

      return sorting.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get('/api/articles');
      setArticles(response.data.data);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError(formatError(err) || t('articles:failedFetch'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingArticle(null);
    setFormData({ name: '', unitMode: 'quantity', description: '' });
    setError('');
    submittingRef.current = false;
    setSubmitting(false);
    setShowModal(true);
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      name: article.name || '',
      unitMode: UNIT_MODES.includes(article.unit_mode) ? article.unit_mode : 'quantity',
      description: article.description || ''
    });
    setError('');
    submittingRef.current = false;
    setSubmitting(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingArticle(null);
    setFormData({ name: '', unitMode: 'quantity', description: '' });
    setError('');
    submittingRef.current = false;
    setSubmitting(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setError('');
    setSubmitting(true);

    if (!formData.name.trim()) {
      setError(t('articles:nameRequired'));
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    if (formData.name.trim().length > 255) {
      setError(t('articles:nameTooLong'));
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    if (!UNIT_MODES.includes(formData.unitMode)) {
      setError(t('articles:unitModeRequired'));
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    const payload = {
      name: formData.name.trim(),
      unitMode: formData.unitMode,
      description: formData.description.trim() || null
    };

    try {
      if (editingArticle) {
        await axios.put(`/api/articles/${editingArticle.id}`, payload);
      } else {
        if (user?.role === 'super_admin' && user.company_id) {
          payload.companyId = user.company_id;
        }
        await axios.post('/api/articles', payload);
      }
      handleCloseModal();
      fetchArticles();
    } catch (err) {
      setError(
        formatError(err)
          || (editingArticle ? t('articles:failedUpdate') : t('articles:failedCreate'))
      );
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleDelete = async (article) => {
    if (submittingRef.current) {
      return false;
    }
    if (!(await confirm(t('articles:confirmDelete', { name: article.name }), { danger: true }))) {
      return false;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await axios.delete(`/api/articles/${article.id}`);
      fetchArticles();
      return true;
    } catch (err) {
      setError(formatError(err) || t('articles:failedDelete'));
      return false;
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const sortedArticles = sortArticles(articles);

  return (
    <div className="articles-page">
      <div className="container container--wide main-content">
        <div className="page-header">
          <h1>{t('articles:pageTitle')}</h1>
          <div className="page-header-actions">
            <ColumnCustomizer
              columnLabels={articleColumnLabels}
              visibleColumns={visibleColumns}
              onChange={(key, checked) => setVisibleColumns((prev) => ({ ...prev, [key]: checked }))}
            />
            {canManage && (
              <button type="button" className="btn btn-primary tasks-header-create-btn" onClick={handleOpenCreate}>
                <span className="material-symbols-outlined" aria-hidden="true">add</span>
                {t('articles:createArticle')}
              </button>
            )}
          </div>
        </div>

        {error && !showModal && <div className="error">{error}</div>}

        {sortedArticles.length > 0 ? (
          <>
            <div className="tasks-list-table-wrap tasks-list-table-desktop">
              <table className="table tasks-list-table">
                <thead>
                  <tr>
                    {visibleColumns.name && renderSortableHeader('name', articleColumnLabels.name)}
                    {visibleColumns.unitMode && renderSortableHeader('unitMode', articleColumnLabels.unitMode)}
                    {visibleColumns.description && renderSortableHeader('description', articleColumnLabels.description)}
                    {showCompanyColumn && visibleColumns.company && renderSortableHeader('company', articleColumnLabels.company)}
                  </tr>
                </thead>
                <tbody>
                  {sortedArticles.map((article) => (
                    <tr
                      key={article.id}
                      className="tasks-list-table-row"
                      onClick={() => handleEdit(article)}
                    >
                      {visibleColumns.name && (
                        <td className="tasks-list-table-title">{article.name}</td>
                      )}
                      {visibleColumns.unitMode && (
                        <td>
                          <span className={`articles-unit-badge articles-unit-badge--${article.unit_mode}`}>
                            {unitModeLabel(article.unit_mode)}
                          </span>
                        </td>
                      )}
                      {visibleColumns.description && (
                        <td className="articles-description-cell">
                          {article.description || t('common:emptyCell')}
                        </td>
                      )}
                      {showCompanyColumn && visibleColumns.company && (
                        <td>{article.company_name || t('common:emptyCell')}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="tasks-card-grid tasks-list-cards-mobile">
              {sortedArticles.map((article) => (
                <article
                  key={article.id}
                  className="tasks-redesign-card"
                  onClick={() => handleEdit(article)}
                >
                  <div className="tasks-redesign-card-body">
                    <div className="tasks-redesign-card-top">
                      <h3 className="tasks-redesign-card-title">{article.name}</h3>
                    </div>
                    <div className="tasks-redesign-card-meta">
                      <span className="tasks-redesign-meta-item">
                        <span className="material-symbols-outlined" aria-hidden="true">category</span>
                        {unitModeLabel(article.unit_mode)}
                      </span>
                      {showCompanyColumn && article.company_name ? (
                        <span className="tasks-redesign-meta-item">
                          <span className="material-symbols-outlined" aria-hidden="true">apartment</span>
                          {article.company_name}
                        </span>
                      ) : null}
                      {article.description ? (
                        <span className="tasks-redesign-meta-item articles-card-description">
                          {article.description}
                        </span>
                      ) : null}
                    </div>
                    {canManage && (
                      <div className="tasks-redesign-card-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          disabled={submitting}
                          onClick={() => handleDelete(article)}
                        >
                          {t('common:delete')}
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </>
        ) : (
          <EmptyState
            icon="category"
            title={t('articles:noArticlesTitle')}
            description={t('articles:noArticlesDescription')}
            actionLabel={canManage ? t('articles:createArticle') : undefined}
            onAction={canManage ? handleOpenCreate : undefined}
          />
        )}

        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingArticle ? t('articles:editArticle') : t('articles:createArticle')}
        >
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="article-name">{t('common:name')} *</label>
              <input
                id="article-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                maxLength={255}
                required
                disabled={submitting}
              />
            </div>
            <fieldset className="form-group articles-unit-mode">
              <legend>{t('articles:unitModeLabel')} *</legend>
              <p className="articles-unit-mode-hint">{t('articles:unitModeHint')}</p>
              <div className="articles-unit-mode-options" role="radiogroup" aria-label={t('articles:unitModeLabel')}>
                {UNIT_MODES.map((mode) => (
                  <label key={mode} className="articles-unit-mode-option">
                    <input
                      type="radio"
                      name="unitMode"
                      value={mode}
                      checked={formData.unitMode === mode}
                      onChange={handleChange}
                      disabled={submitting}
                    />
                    <span>{unitModeLabel(mode)}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="form-group">
              <label htmlFor="article-description">{t('articles:description')}</label>
              <textarea
                id="article-description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                maxLength={2000}
                disabled={submitting}
              />
            </div>
            {error && <div className="error">{error}</div>}
            <div className="modal-actions">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {editingArticle ? t('common:update') : t('common:create')}
              </button>
              {editingArticle && (
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={submitting}
                  onClick={async () => {
                    if (await handleDelete(editingArticle)) {
                      handleCloseModal();
                    }
                  }}
                >
                  {t('common:delete')}
                </button>
              )}
              <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={submitting}>
                {t('common:cancel')}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default Articles;
